import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { paraphraseText } from '../../services/chatApi';
import { Copy, Check } from 'lucide-react';
import { PARAPHRASER_PROMPTS } from './paraphraserPrompts';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const Paraphraser = () => {
    const { t, i18n } = useTranslation();
    const [inputText, setInputText] = useState('');
    const [selectedTone, setSelectedTone] = useState('Academic');
    const { user, setCredits } = useAuth();
    const [paraphrasedText, setParaphrasedText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasCopied, setHasCopied] = useState(false);

    // Get available tones based on current language
    const language = i18n.language.split('-')[0];
    const prompts = PARAPHRASER_PROMPTS[language] || PARAPHRASER_PROMPTS['en'];
    const availableTones = prompts.tones;
    const stripMarkdown = (text) => {
    if (!text) return '';
    
        return text
          // Remove code blocks first (including language specifier)
          .replace(/```[\s\S]*?```/g, '')
          // Remove inline code
          .replace(/`([^`]+)`/g, '$1')
          // Remove images
          .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '$1')
          // Remove links but keep text
          .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
          // Remove bold
          .replace(/(\*\*|__)(.*?)\1/g, '$2')
          // Remove italic
          .replace(/(\*|_)(.*?)\1/g, '$2')
          // Remove strikethrough
          .replace(/~~(.*?)~~/g, '$1')
          // Remove headers
          .replace(/^#{1,6}\s+/gm, '')
          // Remove blockquotes
          .replace(/^\s{0,3}>\s?/gm, '')
          // Remove horizontal rules
          .replace(/^(-{3,}|\*{3,}|_{3,})$/gm, '')
          // Remove list markers
          .replace(/^\s*[-*+]\s+/gm, '')
          .replace(/^\s*\d+\.\s+/gm, '')
          // Remove HTML tags
          .replace(/<[^>]*>/g, '')
          // Clean up multiple newlines
          .replace(/\n{3,}/g, '\n\n')
          // Trim whitespace
          .trim();
      };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputText.trim()) {
            setError(t('paraphraser.errorEnterText'));
            return;
        }

        setIsLoading(true);
        setError(null);
        setParaphrasedText('');

        try {
            const userPromptContent = prompts.user_prompt_template
                .replace('{tone}', selectedTone)
                .replace('{input_text}', inputText);
            
            const messages = [
                { role: 'system', content: prompts.system_prompt },
                { role: 'user', content: userPromptContent }
            ];

            const result = await paraphraseText(messages);
            if (result.paraphrased_text) {
                setParaphrasedText(result.paraphrased_text);
            }

            if (result.new_credits !== undefined) {
                console.log(`Credits updated from paraphraser. New balance: ${result.new_credits}`);
                setCredits(result.new_credits);
            }
        } catch (err) {
            setError(err.message || t('paraphraser.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setInputText('');
        setParaphrasedText('');
        setError(null);
        setSelectedTone('Academic');
    };
    
    const handleCopy = () => {
        if (paraphrasedText) {
            navigator.clipboard.writeText(stripMarkdown(paraphrasedText));
            setHasCopied(true);
            setTimeout(() => setHasCopied(false), 2000); // Reset after 2s
        }
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('paraphraser.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('paraphraser.description')}
                    </p>

                    {/* Input Section */}
                    <form onSubmit={handleSubmit} className="mt-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label htmlFor="tone_select" className="block text-sm font-medium mb-2 text-brand-dark">
                                    {t('paraphraser.toneLabel')}
                                </label>
                                <select
                                    id="tone_select"
                                    value={selectedTone}
                                    onChange={(e) => setSelectedTone(e.target.value)}
                                    className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 rounded-md focus:ring-2 focus:ring-brand-orange focus:outline-none"
                                    disabled={isLoading}
                                >
                                    {availableTones.map((tone) => (
                                        <option key={tone} value={tone}>{tone}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <label htmlFor="input_text" className="block text-sm font-medium mb-2 text-brand-dark">
                            {t('paraphraser.inputLabel')}
                        </label>
                        <textarea
                            id="input_text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder={t('paraphraser.placeholder')}
                            className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 rounded-md focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[150px] resize-y"
                            disabled={isLoading}
                            maxLength={4000}
                        />
                        
                        {error && <p className="text-red-500 mt-2">{error}</p>}

                        <div className="flex items-center space-x-4 mt-4">
                            <button
                                type="submit"
                                className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed"
                                disabled={isLoading}
                            >
                                {isLoading ? t('paraphraser.paraphrasingButton') : t('paraphraser.paraphraseButton')}
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                            >
                                {t('paraphraser.clearButton')}
                            </button>
                        </div>
                    </form>
                    
                    {/* Result Section */}
                    {paraphrasedText && (
                        <div className="mt-10">
                            <h4 className="text-2xl font-semibold">{t('paraphraser.resultTitle')}</h4>
                            <div className="relative mt-4 bg-brand-white p-4 border border-slate-700 rounded-md">
                                <button
                                    onClick={handleCopy}
                                    className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md"
                                    title={t('paraphraser.copyButton')}
                                >
                                    {hasCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                                </button>
                                <pre className="whitespace-pre-wrap font-mono text-sm">
                                    <code>{paraphrasedText}</code>
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Paraphraser;
